import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxAddress, OnyxPhone } from '@onyx/angular';
import {
  ActionHelper,
  AddressHelper,
  OnyxAddressComponent,
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxInformationHeadingComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxMapComponent,
  OnyxMapEvent,
  OnyxMapMarkerType,
  OnyxModalComponent,
  OnyxPhonePipe,
  OnyxTableComponent,
  OnyxToastService,
  OnyxTooltipDirective,
  PhoneHelper,
} from '@onyx/angular';
import { isString } from 'lodash';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { ModalNoteComponent } from '../../../../common/components/modals/modal-note/modal-note.component';
import { SHORT_PAGINATION } from '../../../../common/constants/common/short-pagination';
import { BusinessHoursPipe } from '../../../../common/pipes/business-hours.pipe';
import { DurationPipe } from '../../../../common/pipes/duration.pipe';
import { ModalsStore } from '../../../../common/stores/modals.store';
import { ContractorHelper } from '../../contractors/common/helpers/contractor.helper';
import type { SimplifiedContractor } from '../../contractors/common/interfaces/contractor';
import { ASSIGNED_CONTRACTORS_NOT_FOUND } from '../common/constants/assigned-contractors-not-found';
import { MODAL_ASSIGNED_CONTRACTORS_COLUMNS } from '../common/constants/modal-assigned-contractors-columns';
import { PointsOfInterestRoute } from '../common/enums/points-of-interest-route';
import {
  POINT_OF_INTEREST_HEADER_ACTIONS,
  PointOfInterestHelper,
} from '../common/helpers/point-of-interest.helper';
import type {
  PointOfInterest,
  SimplifiedPointOfInterest,
} from '../common/interfaces/point-of-interest';
import { PointsOfInterestService } from '../common/services/points-of-interest.service';

export type PointOfInterestModalData =
  PointOfInterest | SimplifiedPointOfInterest | string;

enum PointOfInterestModalSection {
  ASSIGNED_CONTRACTORS,
  NOTE,
}

@Component({
  selector: 'app-point-of-interest-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    OnyxMapComponent,
    OnyxInformationRowComponent,
    TranslatePipe,
    DurationPipe,
    OnyxPhonePipe,
    OnyxChipsComponent,
    OnyxTableComponent,
    OnyxDropdownDirective,
    OnyxAddressComponent,
    OnyxTooltipDirective,
    BusinessHoursPipe,
    ModalNoteComponent,
    OnyxInformationHeadingComponent,
    OnyxInformationListComponent,
  ],
  templateUrl: './point-of-interest-modal.component.html',
  styleUrl: './point-of-interest-modal.component.scss',
})
export class PointOfInterestModalComponent {
  protected readonly pointOfInterestData =
    inject<PointOfInterestModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly pointsOfInterestService = inject(PointsOfInterestService);
  private readonly toastService = inject(OnyxToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly pointOfInterestHelper = inject(PointOfInterestHelper);
  private readonly contractorHelper = inject(ContractorHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'pointsOfInterest';
  protected readonly COLUMNS = MODAL_ASSIGNED_CONTRACTORS_COLUMNS;
  protected readonly NOT_FOUND = ASSIGNED_CONTRACTORS_NOT_FOUND;

  protected readonly ActionHelper = ActionHelper;
  protected readonly AddressHelper = AddressHelper;
  protected readonly PhoneHelper = PhoneHelper;
  protected readonly PointOfInterestModalSection = PointOfInterestModalSection;
  protected readonly PointsOfInterestRoute = PointsOfInterestRoute;

  private readonly map = viewChild.required(OnyxMapComponent);
  private readonly uuid: string;

  protected pointOfInterest = signal<PointOfInterest | null>(null);
  protected section = this.modalsStore.getActiveSection(
    PointOfInterestModalComponent.name,
    () => this.sections(),
  );
  protected loading = signal(false);
  protected error = signal(false);
  protected pagination = signal(SHORT_PAGINATION);
  protected pointOfInterest$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected options = computed(() =>
    this.pointOfInterest()
      ? this.pointOfInterestHelper.getOptions(
          this.pointOfInterest()!,
          { close$: this.close$ },
          POINT_OF_INTEREST_HEADER_ACTIONS,
        )
      : null,
  );
  protected sections = computed(() => [
    {
      name: 'labels.assignedContractors',
      value: PointOfInterestModalSection.ASSIGNED_CONTRACTORS,
      count: this.pointOfInterest()
        ? this.pointOfInterest()?.contractors.length
        : undefined,
    },
    { name: 'labels.note', value: PointOfInterestModalSection.NOTE },
  ]);

  constructor() {
    this.uuid = isString(this.pointOfInterestData)
      ? this.pointOfInterestData
      : this.pointOfInterestData.uuid;

    this.pointOfInterest$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.pointsOfInterestService.getPointOfInterest(this.uuid).pipe(
            catchError(() => {
              this.pointOfInterest.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((pointOfInterest) => {
        this.pointOfInterest.set(pointOfInterest);
        this.loading.set(false);
        this.error.set(false);
      });

    if (
      isString(this.pointOfInterestData) ||
      !PointOfInterestHelper.isPointOfInterestType(this.pointOfInterestData)
    ) {
      this.pointOfInterest$.next();
    } else {
      this.pointOfInterest.set(this.pointOfInterestData);
    }

    this.pointsOfInterestService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.pointOfInterest$.next());

    effect(() => {
      const pointOfInterest = this.pointOfInterest();
      if (!pointOfInterest) return;

      afterNextRender(
        () => {
          this.map().dispatch(
            new OnyxMapEvent.AddUpdateMarker({
              id: this.uuid,
              type: OnyxMapMarkerType.POINT_OF_INTEREST,
              coordinates: pointOfInterest.address.coordinates,
            }),
          );
          this.map().dispatch(new OnyxMapEvent.FitContent());
        },
        { injector: this.injector },
      );
    });
  }

  protected edit(): void {
    this.pointOfInterestHelper.edit(this.uuid, { close$: this.close$ });
  }

  protected share(): void {
    const pointOfInterest = this.pointOfInterest();
    if (pointOfInterest) this.pointOfInterestHelper.share(pointOfInterest);
  }

  protected openModal(contractor: SimplifiedContractor): void {
    this.contractorHelper.openModal(contractor);
  }

  protected copyAddress(address: OnyxAddress) {
    ActionHelper.copy(AddressHelper.composeLabel(address), this.toastService);
  }

  protected copyCoordinates(address: OnyxAddress) {
    ActionHelper.copy(
      AddressHelper.composeCoordinatesLabel(address),
      this.toastService,
    );
  }

  protected copyPhone(phone: OnyxPhone) {
    ActionHelper.copy(PhoneHelper.composeLabel(phone), this.toastService);
  }
}
